import { apiClient } from "@api/client";
import { request } from "@api/request";
import type { components } from "@api/schema";
import {
  principalKeyOf,
  usePrincipalKey,
  useSessionStore,
} from "@auth/sessionStore";
import type { IncomingCall } from "@notifications/incomingCallStore";
import { useFrameRefresh } from "@notifications/useFrameRefresh";
import { useQuery } from "@tanstack/react-query";
import { create } from "zustand";

export type CallContext = Required<
  components["schemas"]["PhoneCallContextOut"]
>;

export const contextCallerName = (
  call: IncomingCall,
  context: CallContext | undefined,
): string | null =>
  context
    ? (context.client?.name ?? context.provider_name)
    : call.clientUuid
      ? (call.clientName ?? call.callerName)
      : null;

interface ContextChoice {
  expanded: boolean;
  clientUuid: string | null;
}
interface ContextState {
  choices: Record<string, ContextChoice>;
  setChoice: (key: string, choice: ContextChoice) => void;
}
const EMPTY_CHOICE: ContextChoice = { expanded: false, clientUuid: null };
const useContextState = create<ContextState>((set) => ({
  choices: {},
  setChoice: (key, choice) =>
    set((s) => ({ choices: { ...s.choices, [key]: choice } })),
}));

let choicePrincipal = principalKeyOf(
  useSessionStore.getState().user ?? useSessionStore.getState().clinic,
);
useSessionStore.subscribe(({ user, clinic }) => {
  const next = principalKeyOf(user ?? clinic);
  if (next !== choicePrincipal) {
    choicePrincipal = next;
    useContextState.setState({ choices: {} });
  }
});

const FRAME_TYPES = ["phone_call"];

export const useCallContext = (
  call: Pick<IncomingCall, "callUuid" | "origin">,
) => {
  const principal = usePrincipalKey();
  const key = `${principal}:${call.callUuid}`;
  const choice = useContextState((s) => s.choices[key] ?? EMPTY_CHOICE);
  const setChoice = useContextState((s) => s.setChoice);
  const query = useQuery({
    queryKey: ["call-context", principal, call.callUuid, choice.clientUuid],
    queryFn: ({ signal }) =>
      request(() =>
        apiClient.GET("/phone-calls/{call_uuid}/context", {
          params: {
            path: { call_uuid: call.callUuid },
            query: { client_uuid: choice.clientUuid ?? undefined },
          },
          signal,
        }),
      ),
    select: (data): CallContext => ({
      ...data,
      phone_numbers: data.phone_numbers ?? [],
      patients: data.patients ?? [],
      visits: data.visits ?? [],
      tasks: data.tasks ?? [],
      memos: data.memos ?? [],
      invoices: data.invoices ?? [],
      prior_calls: data.prior_calls ?? [],
      truncated: data.truncated ?? [],
    }),
    enabled: call.origin === "inbound" && principal !== "anon",
    staleTime: 15_000,
    retry: 1,
  });
  useFrameRefresh(FRAME_TYPES, call.callUuid, [
    "call-context",
    principal,
    call.callUuid,
  ]);
  return {
    ...query,
    expanded: choice.expanded,
    setExpanded: (expanded: boolean) => setChoice(key, { ...choice, expanded }),
    selectedClientUuid: choice.clientUuid,
    selectClient: (clientUuid: string | null) =>
      setChoice(key, { ...choice, clientUuid, expanded: true }),
  };
};
